// ***************************************
// ComponentLabel
//
// This component displays a text label using a string or dictionary tag
//
// Copyright 2015 Tingly Games B.V.
// Last Changed: 23-9-2015
// ***************************************
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { ComponentBase } from "./ComponentBase";
import { tgfSettings } from "../tgfSettings";
import { tbGraphics } from "../../engine/tbGraphics";
import { PIXI } from "../../engine/tbPIXI";
import { tbVectorFont } from "../../engine/tbVectorFont";
import { tgfDictionary } from "../tgfDictionary";
tgfSettings.componentDefaults.label = {
    text: "",
    dictionaryKey: "",
    font: undefined,
    align: undefined,
    baseline: undefined,
    fitToBox: true,
    splitLines: false,
    surfaceMargin: 4,
    roundPosition: true,
    components: [],
    onClick: null,
    textWidth: 0,
    textHeight: 0,
    drawDebug: false
};
tgfSettings.styleTranslation = {
    // tbVectorFont > Pixi
    fontFace: "fontFamily",
    fontCase: "fontVariant",
    fillColor: "fill",
    strokeColor: "stroke",
    strokeWidth: "strokeThickness",
    strokeJoin: "lineJoin",
    spacing: "letterSpacing"
};
var ComponentLabel = /** @class */ (function (_super) {
    __extends(ComponentLabel, _super);
    function ComponentLabel(properties) {
        var _this = _super.call(this) || this;
        _this.name = "Label";
        // Initialize properties
        _this.initialize(tgfSettings.componentDefaults.label);
        // Set user properties
        if (properties !== undefined) {
            _this.setProperties(properties);
        }
        return _this;
    }
    // Returns the width of the component. Must be overriden if it is not the width property
    ComponentLabel.prototype.getWidth = function () {
        return this.font.textWidth() - this.surfaceMargin * 2;
    };
    // Returns the height of the component. Must be overriden if it is not the height property
    ComponentLabel.prototype.getHeight = function () {
        return this.font.textHeight() - this.surfaceMargin * 2;
    };
    ComponentLabel.prototype.setText = function (text) {
        this.text = text ? text : this.text;
        this.font.setText(this.text);
    };
    ComponentLabel.prototype.bringToFront = function () {
        this.baseContainer.removeChild(this.font);
        this.baseContainer.addChild(this.font);
    };
    ComponentLabel.prototype.setPosition = function (x, y) {
        this.x = x;
        this.y = y;
        this.font.setPosition(this.containerRelativeX(), this.containerRelativeY());
    };
    // ***************************************
    // Internal methods
    // ***************************************
    // Update the text when the dictionaryKey changes
    ComponentLabel.prototype.findText = function () {
        if (this.dictionaryKey !== "") {
            return tgfDictionary.getText(this.dictionaryKey);
        }
        else {
            return this.text === undefined ? "<UNDEFINED>" : this.text;
        }
    };
    // Create the surface
    ComponentLabel.prototype.createSurface = function () {
        var ww, hh, oldSize, newSize, fnt, oldAlign, oldBaseline, height;
        var text = this.findText();
        // Create the correct font
        if (this.font instanceof tbVectorFont) {
            this.font = this.font.clone();
        }
        else {
            fnt = new tbVectorFont();
            fnt.setProperties(this.font);
            this.font = fnt;
        }
        if (this.breakWords !== undefined) {
            this.font.breakWords = this.breakWords;
            this.font.setStyle();
        }
        if (this.splitLines && this.width) {
            this.font.setWordWrap(this.splitLines, this.width);
        }
        this.font.setText(text);
        // Determine the box for the text
        ww = this.width > 0 ? this.width : this.font.textWidth(text);
        this.textWidth = ww;
        hh = this.height > 0 ? this.height : this.font.textHeight(text);
        this.textHeight = hh;
        // Adapt the alignment when overriden
        oldAlign = this.font.align;
        if (this.align !== undefined) {
            this.font.setAlign(this.align);
        }
        oldBaseline = this.font.baseline;
        if (this.baseline !== undefined) {
            this.font.setBaseline(this.baseline);
        }
        var resetSize = true;
        oldSize = this.font.getFontSize();
        if (this.fitToBox && this.width > 0) {
            height = this.height;
            if (height <= 0) {
                height = hh;
            }
            // textFitSize not working in currentVersion
            newSize = this.font.textFitSize(text, this.width, height, this.splitLines);
            if (newSize < oldSize) {
                this.font.setFontSize(newSize);
                resetSize = false;
            }
        }
        this._xOffset = 0;
        this._yOffset = 0;
        if (this.centerBox) {
            // y-axis
            height = this.height;
            if (height <= 0) {
                height = hh;
            }
            var lineInfo = this.font.lineInfo();
            var lines = lineInfo.lines;
            var lineHeight = lineInfo.lineHeight;
            var areaHeight = lineHeight * lines.length;
            this._yOffset = height / 2 - areaHeight / 2;
        }
        // Restore the size
        if (resetSize) {
            this.font.setFontSize(oldSize);
        }
        // Restore the alignment
        if (this.align === undefined) {
            this.font.setAlign(oldAlign);
        }
        if (this.baseline !== undefined) {
            this.font.setBaseline(oldBaseline);
        }
        this.font.setPosition(this.x, this.y + this._yOffset);
        // this.font.setPosition(this.x, 200);
        this.font.setScale(this.xScale, this.yScale);
        this.font.setRotation(this.angle);
        // if (this.styles) TODO: multistyletext is not defined on pixi
        // {
        // 	this.setStyles(this.styles);
        // 	this.createMultiStyleSurface();
        // }
        this.font.setAlpha(this.alpha);
    };
    ComponentLabel.prototype.setStyles = function (styles) {
        var resultObject = {};
        for (var style in styles) {
            resultObject[style] = {};
            for (var key in styles[style]) {
                if (tgfSettings.styleTranslation[key]) {
                    resultObject[style][tgfSettings.styleTranslation[key]] = styles[style][key];
                }
                else {
                    resultObject[style][key] = styles[style][key];
                }
            }
        }
        this.styles = resultObject;
    };
    ComponentLabel.prototype.setAlpha = function (alpha) {
        this.alpha = alpha;
        this.font.setAlpha(alpha);
    };
    ComponentLabel.prototype.setScale = function (xScale, yScale) {
        this.xScale = xScale;
        this.yScale = yScale;
        this.font.setScale(xScale, yScale);
    };
    // Create the surface
    ComponentLabel.prototype.createMultiStyleSurface = function () {
        if (!this.styles.hasOwnProperty("default")) { // Add default to styles if missing.
            this.styles.default = {};
        }
        if (this.font instanceof tbVectorFont) {
            this.styles.default["fontFace"] = this["font"]["fontFace"];
            this.styles.default["wordWrapWidth"] = this["width"];
            var textStyle = this.font["textStyle"]();
            for (var style in textStyle) {
                if (!textStyle.hasOwnProperty(style)) {
                    continue;
                }
                this.styles["default"][style] = (typeof this.styles["default"][style] === "undefined") ? textStyle[style] : this.styles["default"][style];
            }
            // xOrigin wasn't explicitly defined, overwrite it.
            if (this.xOrigin === undefined) {
                var align = (typeof this.styles["default"]["align"] === "undefined") ? this.align : this.styles["default"]["align"];
                switch (align) {
                    default:
                    case "left":
                        this.xOrigin = 0.0;
                        break;
                    case "center":
                        this.xOrigin = 0.5;
                        break;
                    case "right":
                        this.xOrigin = 1.0;
                        break;
                }
            }
            // yOrigin wasn't explicitly defined, overwrite it.
            if (this.yOrigin === undefined) {
                var baseline = (typeof this["styles"]["default"]["baseline"] === "undefined") ? this.baseline : this["styles"]["default"]["baseline"];
                switch (baseline) {
                    default:
                    case "top":
                        this.yOrigin = 0.0;
                        break;
                    case "middle":
                        this.yOrigin = 0.5;
                        break;
                    case "bottom":
                        this.yOrigin = 1.0;
                        break;
                }
            }
            this.font["remove"](this.baseContainer);
            this.font["free"]();
        }
        this.font = new PIXI.MultiStyleText(this["text"], this["styles"], this.styles["default"]);
        if (this.onClick) {
            this.font["interactive"] = true;
            this.font["on"]("pointerdown", this.onClick);
        }
    };
    ComponentLabel.prototype.doEndStep = function (timePassed) {
        if (this.baseContainer && !this.added) {
            this.baseContainer.addChild(this["font"]);
            this.added = true;
        }
    };
    // ***************************************
    // Preparing, showing and activating
    // ***************************************
    // Processing required when preparing the component for use
    ComponentLabel.prototype.doPrepare = function () {
        var i;
        // this.baseline = 'middle';
        // this.align = 'center';
        // Create the surface
        this.createSurface();
        // draw debug box
        if (this.drawDebug) {
            var w = this.width || this.getWidth();
            var h = this.height || this.getHeight();
            var rect = tbGraphics.drawRectangle(this.x, this.y, w, h, 0xff0000, null, 0, 0.5);
            this.baseContainer.addChild(rect);
        }
        // Add the child components
        for (i = 0; i < this.components.length; i += 1) {
            this.addComponent(this.components[i]);
        }
    };
    // Processing required when freeing the component after use
    ComponentLabel.prototype.doFree = function () {
        if (this.font) {
            if (this.styles && this.font["parent"] && this.font["destroy"]) {
                if (this.font["parent"]) {
                    this.baseContainer.removeChild(this.font);
                }
                this.font["destroy"](true);
                this.font = null;
            }
            else {
                this.font["remove"](this.baseContainer);
                this.font["free"]();
                this.font = null;
            }
        }
    };
    ComponentLabel.prototype.hide = function (alsoChildren) {
        _super.prototype.hide.call(this, alsoChildren);
        if (this.font) {
            if (this.styles) {
                this.font["visible"] = false;
            }
            else {
                this.font["hide"]();
            }
        }
    };
    ComponentLabel.prototype.show = function (alsoChildren) {
        _super.prototype.show.call(this, alsoChildren);
        if (this.font) {
            if (this.styles) {
                this.font["visible"] = true;
            }
            else {
                this.font["show"]();
            }
        }
    };
    // ***************************************
    // Methods called when events must be processed
    // ***************************************
    // Called when a touch is started or if a mouse button is pressed
    ComponentLabel.prototype.doTouchDown = function (finger, x, y) {
        var xx, yy, ww, hh;
        if (this.onClick === null) {
            return;
        }
        // Determine the position of the text surface
        xx = -this.textWidth / 2;
        yy = -this.textHeight / 2;
        if (this.roundPosition) {
            xx = Math.floor(xx);
            yy = Math.floor(yy);
        }
        switch (this.font.align) {
            case "left":
                xx = -this.surfaceMargin;
                break;
            case "right":
                xx = -this.textWidth + this.surfaceMargin;
                break;
        }
        switch (this.font.baseline) {
            case "top":
                yy = -this.surfaceMargin;
                break;
            case "bottom":
                yy = -this.textHeight + this.surfaceMargin;
                break;
        }
        ww = this.textWidth - 2 * this.surfaceMargin;
        hh = this.textHeight - 2 * this.surfaceMargin;
        if (x >= xx && x <= xx + ww && y >= yy && y <= yy + hh) {
            this.onClick(this);
        }
    };
    // Called when the component must be drawn (relative to position 0, 0)
    ComponentLabel.prototype.doDraw = function () {
        if (this.added) {
            if (this.styles) {
                this.font.x = this.containerRelativeX() - this.font.width * this.xOrigin;
                this.font.y = this.containerRelativeY() - this.font.height * this.yOrigin;
            }
            else {
                this.font.setPosition(this.containerRelativeX() + this._xOffset, this.containerRelativeY() + this._yOffset);
            }
        }
    };
    return ComponentLabel;
}(ComponentBase));
export { ComponentLabel };
